import {parsePhoneNumberFromString} from 'libphonenumber-js/min';

export const PRIMARY_PHONE_ERROR_MESSAGE = 'Введите полный номер телефона в международном формате: +375XXXXXXXXX или +7XXXXXXXXXX.';
export const CONTACT_PHONE_ERROR_MESSAGE = 'Введите полный номер телефона в международном формате или корректный добавочный номер.';

function phoneError(message) {
    return new Error(message || PRIMARY_PHONE_ERROR_MESSAGE);
}

function cleanPhoneSource(value) {
    if (value === null || typeof value === 'undefined') {
        return null;
    }
    const source = String(value).trim();
    if (!source) {
        return null;
    }
    return source.replace(/^tel\s*:/i, '').trim();
}

function assertPhoneCharacters(value, errorMessage) {
    if (!/^[+\d\s().,/\\\-\u2010-\u2015\u2212]+$/.test(value)) {
        throw phoneError(errorMessage);
    }
    const pluses = value.match(/\+/g) || [];
    if (pluses.length > 1 || (pluses.length === 1 && value.charAt(0) !== '+')) {
        throw phoneError(errorMessage);
    }
}

function normalizedFullPhoneDigits(value, errorMessage) {
    const source = cleanPhoneSource(value);
    if (!source) {
        return null;
    }
    assertPhoneCharacters(source, errorMessage);

    const startsWithPlus = source.charAt(0) === '+';
    let digits = source.replace(/\D/g, '');
    const startsWithInternationalPrefix = !startsWithPlus && digits.indexOf('00') === 0;
    const isExplicitInternational = startsWithPlus || startsWithInternationalPrefix;
    if (startsWithInternationalPrefix) {
        digits = digits.slice(2);
    }

    if (!isExplicitInternational) {
        if (/^80\d{9}$/.test(digits)) {
            digits = `375${digits.slice(2)}`;
        } else if (/^8\d{10}$/.test(digits)) {
            digits = `7${digits.slice(1)}`;
        } else if (/^0\d{9}$/.test(digits)) {
            digits = `375${digits.slice(1)}`;
        } else if (/^\d{10}$/.test(digits)) {
            digits = `7${digits}`;
        }
    }

    if (!/^[1-9]\d{6,14}$/.test(digits)) {
        throw phoneError(errorMessage);
    }
    return digits;
}

export function normalizePhoneForStorage(value) {
    const digits = normalizedFullPhoneDigits(value, PRIMARY_PHONE_ERROR_MESSAGE);
    if (digits === null) {
        return null;
    }
    if (/^7\d{10}$/.test(digits) || /^375\d{9}$/.test(digits)) {
        return digits;
    }
    throw phoneError(PRIMARY_PHONE_ERROR_MESSAGE);
}

function splitPhoneExtension(value) {
    const source = cleanPhoneSource(value);
    if (!source) {
        return {source: null, extension: null};
    }
    const match = source.match(
        /(?:^|[\s,;()])(?:доб(?:авочный)?\.?|ext(?:ension)?\.?|x|#)\s*[:=.]?\s*(\d{1,6})\s*$/iu
    );
    if (!match) {
        return {source, extension: null};
    }
    return {
        source: source.slice(0, match.index).trim(),
        extension: match[1]
    };
}

export function normalizeContactPhoneForStorage(value) {
    const parts = splitPhoneExtension(value);
    if (!parts.source) {
        if (parts.extension) {
            return `ext:${parts.extension}`;
        }
        return null;
    }

    if (!parts.extension && /^\d{1,6}$/.test(parts.source)) {
        return `ext:${parts.source}`;
    }

    assertPhoneCharacters(parts.source, CONTACT_PHONE_ERROR_MESSAGE);
    let digits;
    try {
        digits = normalizedFullPhoneDigits(parts.source, CONTACT_PHONE_ERROR_MESSAGE);
    } catch (error) {
        const compactDigits = parts.source.replace(/\D/g, '');
        const normalizedDigits = compactDigits.indexOf('00') === 0
            ? compactDigits.slice(2)
            : compactDigits;
        let mainLength = null;
        if (normalizedDigits.indexOf('375') === 0) {
            mainLength = 12;
        } else if (normalizedDigits.indexOf('7') === 0) {
            mainLength = 11;
        }
        if (
            !parts.extension
            && mainLength
            && normalizedDigits.length > mainLength
            && normalizedDigits.length <= mainLength + 6
        ) {
            digits = normalizedFullPhoneDigits(
                normalizedDigits.slice(0, mainLength),
                CONTACT_PHONE_ERROR_MESSAGE
            );
            parts.extension = normalizedDigits.slice(mainLength);
        } else {
            throw error;
        }
    }
    return parts.extension ? `${digits};ext:${parts.extension}` : digits;
}

function phoneParts(value) {
    const source = cleanPhoneSource(value);
    if (!source) {
        return {digits: '', extension: ''};
    }
    const extensionParts = splitPhoneExtension(source);
    if (!extensionParts.source && extensionParts.extension) {
        return {digits: '', extension: extensionParts.extension};
    }
    const mainSource = extensionParts.source || source;
    let digits;
    try {
        digits = normalizedFullPhoneDigits(mainSource, CONTACT_PHONE_ERROR_MESSAGE) || '';
    } catch (error) {
        digits = mainSource.replace(/\D/g, '');
        if (digits.indexOf('00') === 0) {
            digits = digits.slice(2);
        }
        if (/^80\d{9}$/.test(digits)) {
            digits = `375${digits.slice(2)}`;
        } else if (/^8\d{10}$/.test(digits)) {
            digits = `7${digits.slice(1)}`;
        }
    }
    return {
        digits,
		extension: extensionParts.extension || ''
    };
}

export function formatPhone(value, fallback = '') {
    const parts = phoneParts(value);
    if (!parts.digits) {
        return parts.extension ? `доб. ${parts.extension}` : fallback;
    }
    const international = `+${parts.digits}`;
    const number = parsePhoneNumberFromString(international);
    // Use country-specific grouping only when parsing preserves every digit.
    const display = number && number.number === international && number.isPossible()
        ? number.formatInternational()
        : international;
    return `${display}${parts.extension ? ` доб. ${parts.extension}` : ''}`;
}

export function formatPhoneInput(value, mode = 'primary') {
    const source = cleanPhoneSource(value);
    if (!source) {
        return '';
    }
    try {
        const normalized = mode === 'contact'
            ? normalizeContactPhoneForStorage(source)
            : normalizePhoneForStorage(source);
        return formatPhone(normalized && normalized.indexOf('ext:') !== 0 ? `+${normalized}` : normalized);
    } catch (error) {
        return source;
    }
}

export function phoneValidationError(value, options = {}) {
    const source = cleanPhoneSource(value);
    if (!source) {
        return options.required ? 'Укажите номер телефона.' : null;
    }
    try {
        if (options.mode === 'contact') {
            normalizeContactPhoneForStorage(source);
        } else {
            normalizePhoneForStorage(source);
        }
        return null;
    } catch (error) {
        return error.message || (
            options.mode === 'contact'
                ? CONTACT_PHONE_ERROR_MESSAGE
                : PRIMARY_PHONE_ERROR_MESSAGE
        );
    }
}

export function phoneHref(value) {
    const parts = phoneParts(value);
    if (!parts.digits) {
        return null;
    }
    return `tel:+${parts.digits}${parts.extension ? `;ext=${parts.extension}` : ''}`;
}

export function telegramPhoneHref(value) {
    const parts = phoneParts(value);
    return parts.digits ? `https://telegram.me/+${parts.digits}` : null;
}

export function phoneMenuActions(value) {
    const source = String(value || '').trim();
    const result = {display: source, copy: source, call: null, telegram: null};
    if (!source) return result;
    try {
        const normalized = normalizeContactPhoneForStorage(source);
        if (/^ext:\d+$/.test(normalized)) {
            result.display = result.copy = formatPhone(normalized);
            return result;
        }
        const match = normalized.match(/^(\d{7,15})(?:;ext:(\d{1,6}))?$/);
        if (!match) return result;
        const international = `+${match[1]}`;
        const number = parsePhoneNumberFromString(international);
        if (!number || number.number !== international || !number.isPossible()) return result;
        const canonical = `+${normalized}`;
        result.display = formatPhone(canonical);
        result.copy = `${international}${match[2] ? ` доб. ${match[2]}` : ''}`;
        result.call = phoneHref(canonical);
        result.telegram = `https://t.me/${international}`;
    } catch (error) {
        // Preserve incomplete or legacy values for copying, without inventing a link.
    }
    return result;
}

export default {
    formatPhone,
    formatPhoneInput,
    normalizeContactPhoneForStorage,
    normalizePhoneForStorage,
    phoneHref,
    phoneValidationError,
    telegramPhoneHref
};
